import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Box, ArrowDownUp } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const SolidsHandling = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/solids-handling"]?.title} description={seoData["services/solids-handling"]?.description} path="/services/solids-handling" keywords={seoData["services/solids-handling"]?.keywords || []} />

      <PageHero
        badge="Machinery & Equipment"
        title="Solids Handling & Erosion Protection"
        description="Erosion and abrasion protection for solids handling equipment. Ceramic-filled and rubber-based lining systems for hoppers, chutes, cyclones, conveyors, and processing equipment in mining, cement, and industrial applications."
        backgroundKey="solids-handling"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Severe erosion on chutes, hoppers, and transfer points',
              'Cyclone and classifier wear from abrasive slurry',
              'Conveyor belt support structure corrosion and wear',
              'Screen panel and deck plate erosion',
              'Bin and silo wall abrasion and material buildup',
              'Pipe erosion from pneumatic conveying and slurry transport'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-orange-50 rounded-lg">
                <Box className="text-orange-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-orange-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <ArrowDownUp className="text-orange-600 mr-3 flex-shrink-0" size={22} />
                  Chute & Hopper Lining
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Ceramic-filled lining systems for chutes, hoppers, bins, and transfer points. Belzona 1321 and 1811 ceramic coatings provide outstanding resistance to sliding abrasion and impact from ore, aggregate, cement, and minerals.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-orange-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <ArrowDownUp className="text-orange-600 mr-3 flex-shrink-0" size={22} />
                  Cyclone & Classifier Lining
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Internal erosion protection for hydrocyclones, classifiers, and separation equipment. Extend equipment life between shutdowns with abrasion-resistant ceramic and rubber linings.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-orange-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <ArrowDownUp className="text-orange-600 mr-3 flex-shrink-0" size={22} />
                  Conveyor Component Protection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Erosion protection for conveyor structure, idler brackets, skirtboard areas, and transfer point wear plates. Rebuild worn components and apply protective coatings for extended service life.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-orange-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <ArrowDownUp className="text-orange-600 mr-3 flex-shrink-0" size={22} />
                  Slurry Pipe & Pneumatic Line Protection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Internal pipe coating and wear protection for slurry lines, tailings pipes, and pneumatic conveying systems. Ceramic-filled and rubber-based linings for extreme abrasion service.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['Extreme abrasion resistance — ceramic and rubber compound systems',
                  'Extend equipment life 3-5x between replacements',
                  'Fast turnaround for shutdown and outage work',
                  'On-site application — no equipment removal needed',
                  'Reduce material buildup and flow problems',
                  'All solids handling applications — mining, cement, aggregate, grain',
                  'Emergency repair for production-critical equipment',
                  '50-70% cost savings vs. replacement components'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1321 (Ceramic S-Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Sliding abrasion and erosion protection</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1811 (Ceramic Carbide)</p>
                  <p className="text-xs text-slate-500 mt-1">Extreme abrasion resistance</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 2131 (D&A Fluid Elastomer)</p>
                  <p className="text-xs text-slate-500 mt-1">Rubber-like erosion protection</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1111 (Super Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Metal rebuild before lining application</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Equipment Wearing Out? We Protect It.</h2>
          <p className="text-blue-100 mb-8">Contact us for erosion assessment and lining recommendations for your solids handling equipment.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SolidsHandling;
